import type { OpenScenario } from '../../types';
import { formatScenarioName, parseTotalCostRisk, type StrategiesByName } from '../../utils';

export interface ScenarioComparisonRow {
  rowID: string;
  index: number;
  version: number;
  name: string;
  symbol: string;
  requestId: string;
  /** null when cost data is missing (parseTotalCostRisk returned undefined) */
  total: number | null;
  cost: number | null;
  risk: number | null;
}

/**
 * Compute shared max for TOTAL/COST/RISK bar scaling.
 * Returns null when there are no valid numeric values (no data to scale).
 * Filters out null (missing data) when computing max.
 */
export function computeSharedMaxForBars(rows: ScenarioComparisonRow[]): number | null {
  // Collect all total/cost/risk values and keep only valid finite numbers (exclude null/undefined/NaN)
  const allValues = rows
    .flatMap(r => [r.total, r.cost, r.risk])
    .filter((v): v is number => v != null && Number.isFinite(v));
  if (allValues.length === 0) {
    return null;
  }
  const computedMax = Math.max(...allValues);
  return computedMax > 0 ? computedMax : null;
}

/**
 * Compute row max for TOTAL/COST/RISK bar scaling.
 * Returns null when there are no valid numeric values (no data to scale).
 * Filters out null (missing data) when computing max.
 */
export function computeRowMaxForBars(row: Pick<ScenarioComparisonRow, 'total' | 'cost' | 'risk'>): number | null {
  const rowValues = [row.total, row.cost, row.risk].filter((v): v is number => v != null && Number.isFinite(v));
  if (rowValues.length === 0) {
    return null;
  }
  const computedMax = Math.max(...rowValues);
  return computedMax > 0 ? computedMax : null;
}

/**
 * Max of total, cost, risk for a single row. Used for row-relative bar scaling.
 * Returns `null` when no positive numeric values are available.
 */
export function getRowMaxForBars(row: Pick<ScenarioComparisonRow, 'total' | 'cost' | 'risk'>): number | null {
  return computeRowMaxForBars(row);
}

export function buildScenarioComparisonRows(
  openScenarios: OpenScenario[],
  strategiesByName: StrategiesByName
): ScenarioComparisonRow[] {
  return openScenarios.map(({ scenario, originalIndex }) => {
    const costData = parseTotalCostRisk(scenario.Analytics ?? []);
    const sessionId = scenario.SessionIdentifier;
    const requestId = scenario.RequestID;

    return {
      rowID: `${sessionId}:${requestId}:${originalIndex}`,
      index: originalIndex,
      version: originalIndex + 1,
      name: formatScenarioName(scenario, strategiesByName),
      symbol: scenario.Symbol || '-',
      requestId,
      total: costData?.total ?? null,
      cost: costData ? costData.spreadCost + costData.tempCost : null,
      risk: costData?.risk ?? null,
    };
  });
}
